/**
 * Utility functions for CloudFront + Lambda Function URL authentication
 * Handles SHA256 payload hashing as required by AWS documentation:
 * https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-lambda.html
 */

/**
 * Computes SHA256 hash of a string and returns it as a hex string
 */
export async function computeSHA256Hash(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
}

/**
 * Creates headers required for CloudFront + Lambda Function URL requests
 * Includes the x-amz-content-sha256 header with SHA256 hash of the request body
 */
export async function createCloudFrontHeaders(
    body: string,
    additionalHeaders: Record<string, string> = {}
): Promise<Record<string, string>> {
    const contentSha256 = await computeSHA256Hash(body);

    return {
        "Content-Type": "application/json",
        "x-amz-content-sha256": contentSha256,
        ...additionalHeaders
    };
}

export async function fetchWithCloudFrontAuth(
    url: string,
    options: RequestInit & { body?: string } = {}
): Promise<Response> {
    const { body = "", ...restOptions } = options;

    // add SHA256 header for requests with body (POST, PUT, PATCH)
    const method = options.method?.toUpperCase() || "GET";
    const hasBody = ["POST", "PUT", "PATCH"].includes(method) && body;

    const existingHeaders = options.headers instanceof Headers
        ? Object.fromEntries((options.headers as any).entries())
        : (options.headers || {});
    let headers = { ...existingHeaders };

    if (hasBody) {
        const cloudFrontHeaders = await createCloudFrontHeaders(body, headers as Record<string, string>);
        headers = cloudFrontHeaders;
    }

    return fetch(url, {
        ...restOptions,
        method,
        headers,
        body: hasBody ? body : undefined
    });
}
